import { BaseUploadData, InPlaceUpload, InPlaceUploadBaseProps } from "./InPlaceUpload";
import { useEffect, useState } from "react";
import { postKmsData } from "@kaltura/mediaspace-shared-utils";

export interface InPlaceUploadGenericConnectorProps<T> extends InPlaceUploadBaseProps {
    /**
     * params to add to all requests to kms(category id, entry id etc.)
     */
    postParams: Record<string, string>;

    /**
     * kms url to call once the upload is done.
     * postParams and tokenId will be sent to this url.
     */
    createUrl: string;

    /**
     * callback to trigger when the new asset was generated successfully.
     * @param object the new object that was created using the uploaded file
     */
    onCreateDone?: (object: T) => void;
}

/**
 * Image uploader that can be used for purposes other than entry creation.
 */
export const InPlaceUploadGenericConnector = <T,>(props: InPlaceUploadGenericConnectorProps<T>) => {
    const { postParams, createUrl, data: dataProp, onCreateDone, ...rest } = props;

    const [hasError, setHasError] = useState(false);
    const [data, setData] = useState<BaseUploadData | undefined>(dataProp);

    // if prop value changes, update state.
    // this allows additional actions to update the thumbnail.
    useEffect(() => {
        setData(dataProp);
    }, [dataProp]);

    const handleUploadStart = () => {
        // reset previous processing errors
        setHasError(false);
        setData(undefined);
    };

    const handleUploadDone = async (tokenId: string) => {
        // call kms with the upload token to create a new entry
        try {
            const { success, createdObject, ...data } = await postKmsData(
                createUrl,
                { ...postParams, tokenId: tokenId }
            );

            // set error
            !success && setHasError(true);

            // set thumbnail
            setData(data);

            // call media created callback
            success && onCreateDone?.(createdObject);
        }
        catch (e) {
            setHasError(true);
        }
    };

    return (
        <InPlaceUpload
            {...rest}
            onUploadStart={handleUploadStart}
            onUploadDone={handleUploadDone}
            hasError={hasError}
            data={data}
        />
    );
};
